import React, { useState, useMemo, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { ThemeProvider } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { createAppTheme } from './theme';
import './index.css';

const DARK_KEY = 'trm-dark-mode';

function ThemeRoot() {
  const [prefersDark, setPrefersDark] = useState(() => {
    const saved = localStorage.getItem(DARK_KEY);
    if (saved !== null) return saved === 'true';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => {
      if (localStorage.getItem(DARK_KEY) === null) setPrefersDark(e.matches);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
  }, [prefersDark]);

  const toggleDark = () => {
    setPrefersDark(prev => {
      const next = !prev;
      localStorage.setItem(DARK_KEY, String(next));
      return next;
    });
  };

  const theme = useMemo(() => createAppTheme(prefersDark), [prefersDark]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <App prefersDark={prefersDark} toggleDark={toggleDark} />
    </ThemeProvider>
  );
}

const container = document.getElementById('root');
const root = createRoot(container);
root.render(
  <React.StrictMode>
    <ThemeRoot />
  </React.StrictMode>
);
